import { reportError } from '@/lib/report-error-v2'
import { toast } from 'sonner'

type InteractionContext = Record<string, unknown> & {
  component?: string
  user_id?: string
}

function getCurrentRoute(): string | undefined {
  if (typeof window === 'undefined') {
    return undefined
  }

  const { pathname, search } = window.location
  return search ? `${pathname}${search}` : pathname
}

function safeToastSuccess(message: string): void {
  try {
    toast.success(message)
  } catch (error) {
    console.debug('[ButtonInstrumentation] Failed to show success toast', error)
  }
}

function safeToastError(message: string): void {
  try {
    toast.error(message)
  } catch (error) {
    console.debug('[ButtonInstrumentation] Failed to show error toast', error)
  }
}

export async function logButtonInteraction(eventName: string, context: InteractionContext = {}): Promise<void> {
  // TEMPORARILY DISABLED - causing middleware timeouts
  // TODO: Re-enable after fixing API performance
  return Promise.resolve();
}

export async function reportButtonError(
  error: unknown,
  context: { eventName: string; component: string }
): Promise<void> {
  try {
    reportError(error, {
      component: context.component,
      operation: context.eventName,
      category: 'ui_interaction',
      severity: 'high'
    })
  } catch (reportingError) {
    console.debug('[ButtonInstrumentation] Failed to report button error', reportingError)
  }
}

export async function handleButtonAction<T>(
  handler: () => T | Promise<T>,
  options: {
    eventName: string
    component: string
    successMessage?: string | undefined
    errorMessage?: string | undefined
  }
): Promise<void> {
  // Fire logging without blocking the handler
  logButtonInteraction(options.eventName, { component: options.component }).catch(err => 
    console.debug('[ButtonInstrumentation] Logging failed:', err)
  )

  // Run handler immediately, don't wait for logging
  try {
    await handler()
    if (options.successMessage) {
      safeToastSuccess(options.successMessage)
    }
  } catch (error) {
    safeToastError(options.errorMessage ?? 'Action failed. Please try again.')
    await reportButtonError(error, {
      eventName: options.eventName,
      component: options.component
    })
  }
}

